import { useState } from 'react';
import { Search, Shield, ShieldCheck, Eye, Wrench, UserX, Pencil } from 'lucide-react';
import type { PmoUserRecord } from '../../store/useUserStore';
import type { PmoRole } from '../../store/useAuthStore';

const ROLE_CONFIG: Record<PmoRole, { label: string; color: string; icon: typeof Shield }> = {
  super_admin: { label: 'Super Admin', color: 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400', icon: ShieldCheck },
  project_admin: { label: 'Project Admin', color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400', icon: Shield },
  project_resource: { label: 'Resource', color: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400', icon: Wrench },
  read_only: { label: 'Read Only', color: 'text-gray-600 bg-gray-50 dark:bg-gray-700 dark:text-gray-400', icon: Eye },
};

interface UserTableProps {
  users: PmoUserRecord[];
  onEdit: (user: PmoUserRecord) => void;
  onDeactivate: (user: PmoUserRecord) => void;
  currentUserEmail: string;
}

export default function UserTable({ users, onEdit, onDeactivate, currentUserEmail }: UserTableProps) {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<PmoRole | 'all'>('all');

  const filtered = users.filter((u) => {
    const matchesSearch =
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const formatDate = (iso: string) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent/50"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as PmoRole | 'all')}
          className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent/50"
        >
          <option value="all">All Roles</option>
          <option value="super_admin">Super Admin</option>
          <option value="project_admin">Project Admin</option>
          <option value="project_resource">Resource</option>
          <option value="read_only">Read Only</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/50">
              <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">User</th>
              <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Role</th>
              <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Projects</th>
              <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Status</th>
              <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Last Login</th>
              <th className="text-right px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {filtered.map((user) => {
              const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.read_only;
              const RoleIcon = roleConfig.icon;
              const isSelf = user.email === currentUserEmail;

              return (
                <tr
                  key={user._id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors"
                >
                  {/* User */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {user.avatarUrl ? (
                        <img
                          src={user.avatarUrl}
                          alt=""
                          className="w-8 h-8 rounded-full"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-accent/10 text-accent flex items-center justify-center text-xs font-bold">
                          {user.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {user.name}
                          {isSelf && (
                            <span className="ml-2 text-xs text-accent font-normal">(you)</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                      </div>
                    </div>
                  </td>

                  {/* Role */}
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${roleConfig.color}`}
                    >
                      <RoleIcon size={12} />
                      {roleConfig.label}
                    </span>
                  </td>

                  {/* Projects */}
                  <td className="px-4 py-3">
                    {user.role === 'super_admin' || user.role === 'project_admin' ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400 italic">All projects</span>
                    ) : (
                      <span className="text-xs text-gray-600 dark:text-gray-300">
                        {user.assignedProjects?.length || 0} assigned
                      </span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                        user.active
                          ? 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400'
                          : 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${user.active ? 'bg-green-500' : 'bg-red-500'}`} />
                      {user.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>

                  {/* Last Login */}
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300 text-xs">
                    {formatDate(user.lastLogin)}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => onEdit(user)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-accent transition-colors"
                        title="Edit user"
                      >
                        <Pencil size={14} />
                      </button>
                      {!isSelf && user.active && (
                        <button
                          onClick={() => onDeactivate(user)}
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600 transition-colors"
                          title="Deactivate user"
                        >
                          <UserX size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}

            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
        {filtered.length} of {users.length} users
      </p>
    </div>
  );
}
